// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Header, Button, Form, Message, Icon, Segment, Dropdown } from 'semantic-ui-react';
import { policyTypeApi, PolicyType } from '../api/policyTypeApi';
import { providerApi, Provider } from '../api/providerApi';
import PolicyTypeBrokerageModal from '../components/PolicyTypeBrokerageModal';

function PolicyTypeFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [providers, setProviders] = useState<Provider[]>([]);
  const [formData, setFormData] = useState<PolicyType>({
    providerId: '',
    name: '',
    code: '',
    description: '',
    category: 'PENSION',
    isActive: true,
    overrideEmail: '',
    emailOverrideReason: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [savedPolicyTypeId, setSavedPolicyTypeId] = useState<string | undefined>(undefined);

  const categoryOptions = [
    { key: 'PENSION', text: 'Pension', value: 'PENSION' },
    { key: 'INSURANCE', text: 'Insurance', value: 'INSURANCE' },
    { key: 'INVESTMENT', text: 'Investment', value: 'INVESTMENT' },
    { key: 'BANKING', text: 'Banking', value: 'BANKING' }
  ];

  useEffect(() => {
    loadProviders();
    if (isEditMode && id) {
      loadPolicyType(id);
    }
  }, [id, isEditMode]);

  const loadProviders = async () => {
    try {
      const data = await providerApi.getAll({ isActive: true, size: 1000 });
      setProviders(data.content);
    } catch (err) {
      console.error('Failed to load providers:', err);
    }
  };

  const loadPolicyType = async (policyTypeId: string) => {
    try {
      setLoading(true);
      const data = await policyTypeApi.getById(policyTypeId);
      setFormData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load policy type');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      if (!formData.providerId) {
        setError('Please select a provider');
        setLoading(false);
        return;
      }

      // Prepare data - convert empty strings to undefined for optional fields
      const submitData = {
        ...formData,
        overrideEmail: formData.overrideEmail?.trim() || undefined,
        emailOverrideReason: formData.emailOverrideReason?.trim() || undefined
      };

      if (isEditMode && id) {
        await policyTypeApi.update(id, submitData);
        setSuccess(true);
        setSavedPolicyTypeId(id);
        setModalOpen(true);
      } else {
        const created = await policyTypeApi.create(submitData);
        setSuccess(true);
        setSavedPolicyTypeId(created.id);
        setModalOpen(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save policy type');
    } finally {
      setLoading(false);
    }
  };

  const selectedProvider = providers.find(p => p.id === formData.providerId);

  const handleModalClose = () => {
    setModalOpen(false);
    navigate('/policy-types');
  };

  const providerOptions = providers.map(p => ({
    key: p.id!,
    text: p.name,
    value: p.id!
  }));

  return (
    <Container style={{ marginTop: '50px', paddingBottom: '50px' }}>
      <Button basic onClick={() => navigate('/policy-types')} style={{ marginBottom: '20px' }}>
        <Icon name='arrow left' /> Back to Policy Types
      </Button>

      <Header as='h1'>
        <Icon name='file alternate' />
        <Header.Content>
          {isEditMode ? 'Edit Policy Type' : 'New Policy Type'}
        </Header.Content>
      </Header>

      {error && (
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {success && (
        <Message positive>
          <Message.Header>Success</Message.Header>
          <p>Policy type {isEditMode ? 'updated' : 'created'} successfully!</p>
        </Message>
      )}

      <Segment>
        <Form loading={loading}>
          <Form.Field required>
            <label htmlFor="policy-type-provider">Provider</label>
            <Dropdown
              id="policy-type-provider"
              placeholder='Select Provider'
              fluid
              selection
              search
              options={providerOptions}
              value={formData.providerId}
              onChange={(_e, data) => setFormData({ ...formData, providerId: data.value as string })}
              disabled={isEditMode}
            />
          </Form.Field>

          <Form.Group widths='equal'>
            <Form.Input
              label='Name'
              placeholder='Policy type name (e.g., Personal Retirement Bond)'
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <Form.Input
              label='Code'
              placeholder='Uppercase code (e.g., PERSONAL_RETIREMENT_BOND)'
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
              required
            />
          </Form.Group>

          <Form.TextArea
            label='Description'
            placeholder='Policy type description'
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />

          <Form.Field required>
            <label htmlFor="policy-type-category">Category</label>
            <Dropdown
              id="policy-type-category"
              placeholder='Select Category'
              fluid
              selection
              options={categoryOptions}
              value={formData.category}
              onChange={(_e, data) => setFormData({ ...formData, category: data.value as PolicyType['category'] })}
            />
          </Form.Field>

          <Form.Checkbox
            label='Active'
            checked={formData.isActive}
            onChange={(_e, data) => setFormData({ ...formData, isActive: !!data.checked })}
          />

          <Message info icon>
            <Icon name='info circle' />
            <Message.Content>
              <Message.Header>Email Override (Optional)</Message.Header>
              <p>
                Configure a specific email address for LOA notifications for all policies of this type.
                If not set, notifications will be sent to the provider's default email.
                This is useful when different policy types from the same provider are managed by different departments.
              </p>
            </Message.Content>
          </Message>

          <Form.Input
            label='Override Email'
            placeholder='e.g., term-life@manulife.com'
            value={formData.overrideEmail}
            onChange={(e) => setFormData({ ...formData, overrideEmail: e.target.value })}
            type='email'
            icon='mail'
            iconPosition='left'
          />

          <Form.TextArea
            label='Email Override Reason'
            placeholder='Explain why this policy type routes to a different email (e.g., Term life policies managed by specialized department)'
            value={formData.emailOverrideReason}
            onChange={(e) => setFormData({ ...formData, emailOverrideReason: e.target.value })}
            rows={2}
          />

          <Button
            primary
            onClick={handleSubmit}
            disabled={!formData.name || !formData.code || !formData.providerId || loading}
          >
            <Icon name='save' /> {isEditMode ? 'Update' : 'Create'} Policy Type
          </Button>
          <Button onClick={() => navigate('/policy-types')}>Cancel</Button>
        </Form>
      </Segment>

      {formData.providerId && (
        <PolicyTypeBrokerageModal
          open={modalOpen}
          onClose={handleModalClose}
          providerId={formData.providerId}
          providerName={selectedProvider?.name || ''}
          highlightedPolicyTypeId={savedPolicyTypeId}
        />
      )}
    </Container>
  );
}

export default PolicyTypeFormPage;
